import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Utensils } from "lucide-react";

const FeaturedDishes = ({ items, loading, sectionRef, l }) => {
  const { t } = useTranslation();

  return (
    <section className="py-20 bg-white" data-testid="featured-dishes-section" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="section-header reveal">
          <span className="text-[#E07A5F] font-medium tracking-wider uppercase text-sm">
            {t("menu.featured.badge")}
          </span>
          <h2 className="mt-3">{t("menu.featured.title")}</h2>
          <p>{t("menu.featured.subtitle")}</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {items.map((item, idx) => (
              <Card key={item.id} className="bg-[#F4F1DE] border-0 overflow-hidden card-lift" data-testid={`menu-item-${idx}`}>
                {item.image_url && (
                  <div className="h-48 img-hover-zoom">
                    <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                )}
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-xl font-semibold text-[#3D405B] font-['Playfair_Display']">{l(item, "name")}</h3>
                    {item.is_vegetarian && (
                      <Badge className="bg-[#2D6A4F] text-white text-xs">{t("menu.veg")}</Badge>
                    )}
                  </div>
                  <p className="text-[#6B705C] text-sm mb-4">{l(item, "description")}</p>
                  <span className="text-sm text-[#6B705C] capitalize">{item.category}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const AllDishes = ({ items, loading, sectionRef, l }) => {
  const { t } = useTranslation();

  return (
    <section className="py-20 bg-[#F4F1DE]" data-testid="all-menu-section" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="section-header reveal">
          <span className="text-[#E07A5F] font-medium tracking-wider uppercase text-sm">
            {t("menu.allDishes.badge")}
          </span>
          <h2 className="mt-3">{t("menu.allDishes.title")}</h2>
          <p>{t("menu.allDishes.subtitle")}</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {items.map((item, idx) => (
              <Card key={item.id} className="bg-white border-0 shadow-sm overflow-hidden card-subtle-hover" data-testid={`all-menu-item-${idx}`}>
                {item.image_url ? (
                  <div className="h-44 img-hover-zoom">
                    <img src={item.image_url} alt={l(item, "name")} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="h-32 bg-[#E07A5F]/5 flex items-center justify-center">
                    <Utensils className="w-10 h-10 text-[#E07A5F]/30" />
                  </div>
                )}
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-1">
                    <h4 className="font-semibold text-[#3D405B] font-['Playfair_Display']">{l(item, "name")}</h4>
                    {item.is_vegetarian && (
                      <Badge className="bg-[#2D6A4F] text-white text-xs shrink-0 ml-2">{t("menu.veg")}</Badge>
                    )}
                  </div>
                  <span className="text-xs text-[#E07A5F] capitalize font-medium">{item.category}</span>
                  <p className="text-sm text-[#6B705C] line-clamp-2 mt-2">{l(item, "description")}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export { FeaturedDishes, AllDishes };
